
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Client } from "./types";
import { useQueryClient } from "@tanstack/react-query";
import { logErrorToast } from "@/utils/toastLogger";

interface ClientUpdateFormProps {
  client: Client;
  onCancel: () => void;
  onSuccess: () => void;
}

function isErrorWithMessage(error: unknown): error is { message: string } {
  return typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message: unknown }).message === 'string';
}

export const ClientUpdateForm = ({ client, onCancel, onSuccess }: ClientUpdateFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: client.name,
    industry: client.industry || "",
    website: client.website || "",
    contact_email: client.contact_email || "",
    contact_phone: client.contact_phone || "",
    corporate_phone: client.corporate_phone || "",
    address: client.address || "",
    notes: client.notes || "",
    status: client.status || "active",
  });
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!formData.name.trim()) {
        throw new Error("Company name is required");
      }
      
      if (formData.contact_email && !formData.contact_email.includes("@")) {
        throw new Error("Invalid email format");
      }
      
      const { error } = await supabase
        .from("clients")
        .update({
          name: formData.name,
          industry: formData.industry || null,
          website: formData.website || null,
          contact_email: formData.contact_email || null,
          contact_phone: formData.contact_phone || null,
          corporate_phone: formData.corporate_phone || null,
          address: formData.address || null,
          notes: formData.notes || null,
          status: formData.status || "active",
        })
        .eq("id", client.id);
        
      if (error) throw error;
      
      onSuccess();
    } catch (error: unknown) {
      console.error("Error updating client:", error);
      const errorMessage = isErrorWithMessage(error) ? error.message : "Failed to update client";
      logErrorToast(errorMessage, error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Update Client</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_email">Contact Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="corporate_phone">Corporate Phone</Label>
              <Input
                id="corporate_phone"
                value={formData.corporate_phone}
                onChange={(e) => setFormData({ ...formData, corporate_phone: e.target.value })}
                placeholder="Main company phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update Client"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
